import React, { useCallback, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { IProject, ITeamMember } from '@types';
import { TeamsTableHeader } from '../constants/headers';
import { selectPermissions } from '@redux/accounts/accounts.selectors';
import { useAppSelector } from '@redux/hooks';
import { filterProjects, getVisibleHeaders } from './helpers';
import { useCollapsedGroups, useIsPhone } from '@hooks';
import { NoMatchesState } from 'components/common/NoMatchesState/NoMatchesState';
import { TeamsCardList } from './TeamsCardList';
import { TeamsTable } from './TeamsTable';

type Props = {
  projects: IProject[];
  query: string;
  accountDeliveryManager?: ITeamMember;
};

export const TeamsList: React.FC<Props> = ({
  projects,
  query,
  accountDeliveryManager,
}) => {
  const permissions = useAppSelector(selectPermissions);
  const isPhone = useIsPhone();
  const fold = useCollapsedGroups(Boolean(query.trim()));
  const hasDeliveryManager =
    !!accountDeliveryManager ||
    projects.some(({ deliveryManager }) => deliveryManager);
  const headers = React.useMemo(
    () => getVisibleHeaders(permissions, hasDeliveryManager),
    [permissions, hasDeliveryManager],
  );
  const [sortKey, setSortKey] = useState<TeamsTableHeader>(headers[0]);
  React.useEffect(() => {
    setSortKey((prev) => (headers.includes(prev) ? prev : headers[0]));
  }, [headers]);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  const handleSort = useCallback(
    (key: TeamsTableHeader) => {
      if (sortKey === key) {
        setSortDir((dir) => (dir === 'asc' ? 'desc' : 'asc'));
      } else {
        setSortKey(key);
        setSortDir('asc');
      }
    },
    [sortKey],
  );

  if (projects.length === 0) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minHeight={520}
      >
        <img
          src="https://ae-cp-static-files.s3.eu-central-1.amazonaws.com/client-dashboard/team.png"
          alt="No teams"
          width="172"
          height="172"
        />
        <Typography variant="h2" align="center">
          There are no team members from AgileEngine
        </Typography>
        <Typography variant="body2" color="text.secondary" align="center">
          Please contact your assigned delivery manager to change that
        </Typography>
      </Box>
    );
  }

  const visibleProjects = filterProjects(projects, query);

  if (visibleProjects.length === 0)
    return (
      <NoMatchesState
        title="No team members match your search"
        description="Clear the search to see the whole team"
      />
    );

  return isPhone ? (
    <TeamsCardList
      projects={visibleProjects}
      visibleHeaders={headers}
      fold={fold}
    />
  ) : (
    <TeamsTable
      projects={visibleProjects}
      headers={headers}
      sortKey={sortKey}
      sortDir={sortDir}
      onSort={handleSort}
      accountDeliveryManager={accountDeliveryManager}
      fold={fold}
    />
  );
};
